import React from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { motion } from "motion/react";

interface NavigationLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "mobile";
  icon?: React.ComponentType<{ className?: string }>;
  active?: boolean;
  onClick?: () => void;
}

export const NavigationLink: React.FC<NavigationLinkProps> = ({
  to,
  children,
  className,
  variant = "default",
  icon: Icon,
  active = false,
  onClick,
}) => {
  const baseClasses = "font-medium transition-colors duration-200 rounded-md";
  
  const variantClasses = {
    default: clsx(
      "text-sm px-3 py-2",
      active
        ? "text-gray-900 dark:text-gray-100"
        : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100",
      "hover:bg-gray-50 dark:hover:bg-gray-800/50"
    ),
    mobile: clsx(
      "text-md px-2 py-2 gap-x-2 flex items-center w-full",
      "text-gray-900 hover:text-gray-700 dark:text-zinc-100 dark:hover:text-white",
      "hover:bg-gray-200 dark:hover:bg-zinc-700"
    ),
  };

  const combinedClasses = clsx(baseClasses, variantClasses[variant], className);

  const content = (
    <>
      {Icon && variant === "mobile" && (
        <Icon className="w-5 h-5 inline-block text-gray-500" />
      )}
      <span className={variant === "mobile" ? "inline-block" : undefined}>
        {children}
      </span>
    </>
  );

  if (variant === "mobile") {
    return (
      <Link to={to} className={combinedClasses} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <Link to={to} onClick={onClick}>
      <span className={combinedClasses}>{content}</span>
    </Link>
  );
};

interface NavigationButtonProps {
  active?: boolean;
  expandable?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export const NavigationButton: React.FC<NavigationButtonProps> = ({
  active = false,
  expandable = false,
  children,
  className,
  onClick,
}) => {
  return (
    <span
      onClick={onClick}
      className={clsx(
        "group text-sm font-medium transition-colors duration-200 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer inline-flex items-center",
        active
          ? "text-gray-900 dark:text-gray-100"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100",
        className
      )}
    >
      {children}
      {expandable && (
        <motion.div
          animate={{ rotate: active ? 180 : 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <svg
            className={clsx(
              "ml-1 h-4 w-4 group-hover:text-gray-900 dark:group-hover:text-gray-100",
              active
                ? "text-gray-900 dark:text-gray-100"
                : "text-gray-500 dark:text-gray-400"
            )}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </motion.div>
      )}
    </span>
  );
}; 